import { reactive, ref, computed, onBeforeMount } from '@vue/composition-api'
import store from '@/store'
import router from '@/router'
import * as common from '@/util/common'
import * as apiCommon from '@/api/common'
import * as apiQA from '@/api/qa'
import * as apiSearchCondition from '@/api/search-condition'
import { propagateCheckedRecursively, setParentNodeCheckStatusRecursively, getIdMapListOfCheckedBooks } from '@/logic/common/CommonQA'
import * as _ from 'lodash'
import * as logicCommonQA from '@/logic/common/CommonQA'
import { SCROLL_INTERVAL_DELAY, SCROLL_INTERVAL_MAX_COUNT, PREFEX_EXTRACT_CONDITION } from '@/util/constants'
import { searchByKeyword, getSearchText, getIsFuzzy } from '@/util/search'

/**
 * QA一覧（検索結果）画面
 */
export const getGroup = (props, context) => {

  onBeforeMount(() => {
    // 初期表示
    group1.init()
  })

  const group1 = reactive({

    // モード(list: 一覧表示モード、search: キーワード検索モード )
    mode: 'list',

    // キーワード検索中判定
    isKeywordSearch: computed(() => {
      // キーワードを入力した検索
      return (group1.isTotalItemLoaded && group1.search_text !== '' && group1.mode === 'search')
    }),

    // 詳細検索実行フラグ(false:簡易検索、true:詳細検索)
    // 詳細検索ウィンドウから実行された場合、ページネーション時に詳細検索を実行するか判断するのに使用する
    // 詳細検索ウィンドウで検索実行時にtrueに設定される
    isSearchDetail: computed(() => {
      return store.state.qa.isSearchDetail
    }),

    // ### ヘッダエリア ###

    // キーワード入力欄に入力した文字列
    search_text: computed(() => {
      return getSearchText(group1.searchCategory)
    }),

    // 表記の揺れを含む：0(曖昧検索),1(一致検索)
    is_fuzzy: computed(() => {
      return getIsFuzzy(group1.searchCategory)
    }),

    // 検索カテゴリ（選択肢）
    searchCategory: computed({
      get: () => {
        return store.state.search.searchCategory
      },
      set: (searchCategory) => {
        store.dispatch('search/setSearchCategory', searchCategory)
      }
    }),

    // ### 絞り込みエリア ###
    packagesAndProductsDataExists: false, // パッケージ＆商品データ存在フラグ

    // 絞り込みエリアのヒット件数表示フラグ
    isSearchCountShown: computed(() => {

      if (group1.isSearchDetail) {
        // 詳細検索の場合
        // キーワード検索のキーワードが入力されているまたは、
        // AND/OR検索のキーワードが１つ以上入力されている場合は、
        // ヒット件数を表示する
        let keywordExists = false
        if (
          typeof store.state.search.qa_extract !== 'undefined' &&
          typeof store.state.search.qa_extract.search_text !== 'undefined'
        ) {
          const searchText = store.state.search.qa_extract.search_text ?? ''
          if (Array.isArray(searchText)) {
            for (let i = 0; i < searchText.length; i++) {
              for (let k = 0; k < searchText[i].or.length; k++) {
                let text = searchText[i].or[k] ?? ''
                text = text.trim()
                if (text !== '') {
                  keywordExists = true
                  break
                }
              }
              if (keywordExists) { break }
            }
          } else {
            const text = searchText.trim()
            if (text !== '') {
              keywordExists = true
            }
          }
        }
        return keywordExists
      } else {
        // 簡易検索の場合はキーワードが入力されている場合
        const text = group1.search_text ?? ''
        return (group1.mode === 'search' && text !== '')
      }
    }),

    // ツリーの各ノートの親ノードの参照を格納するオブジェクト
    parentNodeMap: {},
    // パッケージ＆商品ツリーデータ
    packagesAndProductsAreaTreeData: {},
    // 単体書籍リスト
    packagesAndProductsAreaBooks: [],

    single_books: [], // 単体書籍（データバインディング用）

    // データロード完了フラグ
    isListPackagesAndProductsLoaded: false,

    // ### 検索結果一覧エリア ###
    totalItems: 0, // 全データ件数
    totalItemsDisp: computed(() => {
      return common.breakComma(group1.totalItems)
    }),
    isTotalItemLoaded: false, // データ件数ロード完了フラグ

    // 表示順の表示用
    orderDisp: computed(() => {
      if (store.state.qa.list_order === 'score') {
        return '関連度順'
      } else if (store.state.qa.list_order === 'book') {
        return '書籍順'
      } else {
        return ''
      }
    }),

    listExists: false, // リスト存在フラグ
    list: [],

    // 1ページ当たりの表示件数
    perPage: computed(() => {
      return store.state.qa.list_per_page
    }),

    // 現在のページ
    currentPage: computed({
      get: () => {
        let page = store.state.qa.page
        if (page < 1) {
          page = 1
        }
        return page
      },
      set: (value) => {
        store.dispatch('qa/setPage', value)
      }
    }),
    isPaginate: false, // ページネートフラグ(初回検索のみfalse、以降ページ切り替え後はずっとtrue)

    isPaginationShown: false, // ページネーション表示フラグ（2ページ以上の場合に表示）

    // リスト設定ロード完了フラグ
    isQAListSettingsLoaded: false,
    // データロード完了フラグ
    isQAListLoaded: false,

    // 一覧項目で非表示にする項目のフラグ
    is_hidden_text: computed(() => {
      return (store.state.qa.list_hidden_keys.indexOf('text') >= 0)
    }),
    is_hidden_current: computed(() => {
      return (store.state.qa.list_hidden_keys.indexOf('current') >= 0)
    }),
    is_hidden_note: computed(() => {
      return (store.state.qa.list_hidden_keys.indexOf('note') >= 0)
    }),
    is_hidden_format: computed(() => {
      return (store.state.qa.list_hidden_keys.indexOf('format') >= 0)
    }),
    is_hidden_book: computed(() => {
      return (store.state.qa.list_hidden_keys.indexOf('book') >= 0)
    }),

    /**
     * リスト表示時のパラメータの初期化
     */
    initializeParametersForList: () => {
      group1.packagesAndProductsDataExists = false
      group1.isListPackagesAndProductsLoaded = false
      group1.isTotalItemLoaded = false
      group1.listExists = false
      group1.isPaginationShown = false
      group1.isQAListSettingsLoaded = false
      group1.isQAListLoaded = false
    },

    /**
     * 初期表示
     */
    init: () => {

      // 検索カテゴリを設定
      store.dispatch('search/setSearchCategory', 'qa')

      // リスト表示時のパラメータの初期化
      group1.initializeParametersForList()

      if (
        (typeof store.state.search.qa !== 'undefined' && Object.keys(store.state.search.qa).length > 0) ||
        (typeof store.state.qa.idMapListOfCheckedBooks !== 'undefined' && store.state.qa.idMapListOfCheckedBooks.length > 0)
      ) {
        // 検索条件が設定されている場合（ダッシュボード、メニュー、保存検索条件画面、検索履歴画面から来た場合）
        
        if (store.state.search.qa.search_type) {
          // search_typeが設定されている場合は、詳細検索
          store.dispatch('qa/setSearchDetail', true)
        } else {
          // search_typeが設定されていない場合は、簡易検索
          store.dispatch('qa/setSearchDetail', false)
        }


        // ### 絞り込み表示用の検索条件 ###
        let dataExtract 
        if (typeof store.state.search.qa_extract !== 'undefined' && Object.keys(store.state.search.qa_extract).length > 0) {
          // 検索一覧表示用の検索条件と、絞り込み表示用の検索条件を分離する対応をする後のバージョン用
          dataExtract = store.state.search.qa_extract
        } else {
          // 検索一覧表示用の検索条件と、絞り込み表示用の検索条件を分離する対応をする前のバージョン用
          // （保存検索条件画面、検索履歴画面の検索条件がDBに保存されているため、古いデータ形式に対応させる必要があるため）
          dataExtract = store.state.search.qa
        }
        // QA一覧APIの詳細検索用のリクエストパラメータを生成する
        const searchExtractParams = logicCommonQA.createQAListParamsOfSearchDetails(dataExtract)
        // 検索条件をストアに保存
        store.dispatch('search/setQASearchExtractParam', searchExtractParams)


        // ### 検索結果一覧表示用の検索条件 ###
        if (store.state.qa.isSearchDetail) {
          // 詳細検索の場合
          const data = store.state.search.qa

          // QA一覧APIの詳細検索用のリクエストパラメータを生成する
          const searchParams = logicCommonQA.createQAListParamsOfSearchDetails(data)
          // 検索条件をストアに保存
          store.dispatch('search/setQASearchParam', searchParams)

          // UIに反映
          if (data.search_type === 'keyword') {
            // キーワード検索の場合のみ（AND/OR検索はキーワードを設定しない）  

            // // 検索UI(PCSearchUI/SPSearchUI)の検索テキストと曖昧フラグを更新する
            // const searchTextInput = data.search_text ?? ''
            // const isFuzzyInput = data.is_fuzzy ?? 0
            // store.dispatch('searchUI/setSearchInputOfSearchUI', { searchText: searchTextInput, isFuzzy: isFuzzyInput })
            // store.dispatch('searchUI/setSearchInputOfSearchUIParam', { searchCategory: group1.searchCategory, searchText: searchTextInput, isFuzzy: isFuzzyInput })

          }
        } else {
          // 簡易検索の場合

          // 詳細検索実行フラグをtrueに設定
          store.dispatch('qa/setSearchDetail', false)

          // 検索条件
          const data = store.state.search.qa

          // // 検索UI(PCSearchUI/SPSearchUI)の検索テキストと曖昧フラグを更新する
          // const searchTextInput = data.search_text ?? ''
          // const isFuzzyInput = data.is_fuzzy ?? 0
          // store.dispatch('searchUI/setSearchInputOfSearchUI', { searchText: searchTextInput, isFuzzy: isFuzzyInput })
          // store.dispatch('searchUI/setSearchInputOfSearchUIParam', { searchCategory: group1.searchCategory, searchText: searchTextInput, isFuzzy: isFuzzyInput })

          if (typeof data.product_books !== 'undefined') {
            // store.state.search.qaにproduct_booksが設定されている場合
            // 絞り込みのチェック状態を復元
            const idMapListOfCheckedBooks = data.product_books
            store.dispatch('qa/setIdMapListOfCheckedBooks', idMapListOfCheckedBooks)
          } else {
            const fromNameForQAList = store.state.qa.fromNameForQAList
            if (
              fromNameForQAList === 'PCHistoryOfSearch' ||
              fromNameForQAList === 'SPHistoryOfSearch' ||
              fromNameForQAList === 'PCSearchConditionList' ||
              fromNameForQAList === 'SPSearchConditionList'
            ) {
              // 保存検索条件画面、検索履歴画面から遷移してきた場合で、
              // store.state.search.qaにproduct_booksが設定されていない場合は、
              // 絞り込みのチェック状態をクリアする
              store.dispatch('qa/setIdMapListOfCheckedBooks', {})
            }
          }
        }

        // モードを「search: キーワード検索モード」に設定
        group1.mode = 'search'

        // 検索
        group1.searchWithOptions({ is_init: true })
      } else {
        // 検索条件が設定されていない場合

        // モードを「list: 一覧表示モード」に設定
        group1.mode = 'list'

        // 絞り込みエリア（パッケージ・商品一覧エリア）を更新
        group1.updatePackageAndProductsArea(true)

        // メインエリア(検索結果一覧)の表示処理
        group1.updateSearchResultArea()
      }
    },

    /**
     * 絞り込みエリア（パッケージ・商品一覧エリア）を更新
     */
    updatePackageAndProductsArea: (isInit) => {

      group1.isListPackagesAndProductsLoaded = false

      // パッケージ・商品一覧APIをコール
      const basicParams = {}
      basicParams.is_trial = 0 // 0=通常
      basicParams.is_only_contact = 1 // 1=契約商品のみ
      basicParams.search_range = 3 // キーワード検索対象範囲：3=QA

      // 検索パラメータ(一覧API、件数取得API共通)
      let searchParams = {}
      if (typeof store.state.search.qa_extract !== 'undefined') {
        // 絞り込み表示用検索条件の呼び出し
        // 簡易検索の場合は、QAList.searchWithOptions()メソッド内、
        // 詳細検索の場合は、QASearchDetail.search()メソッド内で、
        // 次の記述により、検索条件をストアに保存している
        // store.dispatch('search/setQASearchExtractParam', searchParams)
        searchParams = store.state.search.qa_extract
      }

      const params = { ...basicParams, ...searchParams }

      apiQA.listPackagesAndProductsForSearch(params)
        .then(response => {
          if (response) {
            const { data } = response

            // パッケージ商品リスト
            const packages = data.packages ?? []

            // 単体書籍リスト
            const books = data.books ?? []

            // パッケージ＆商品データ存在フラグ
            group1.packagesAndProductsDataExists = (packages.length + books.length > 0)


            // 検索対象の商品IDのリスト
            const searchProductIds = []
            for (let i = 0; i < packages.length; i++) {
              for (let k = 0; k < packages[i].products.length; k++) {
                const productId = packages[i].products[k].product_id
                searchProductIds.push(productId)
              }
            }

            // 書籍一覧APIをコール
            const basicParams = {}
            basicParams.search_range = 3 // キーワード検索対象範囲：3=QA
            basicParams.product_ids = searchProductIds
      
            const params = { ...basicParams, ...searchParams }

            apiQA.listBooksForSearch(params)
              .then(response => {
                if (response) {
                  const { data } = response

                  const products2 = data.products ?? []

                  // パッケージ・商品一覧APIで取得したパッケージ＆商品と
                  // 書籍一覧APIで取得した書籍を結合する
                  for (let i = 0; i < packages.length; i++) {
                    for (let k = 0; k < packages[i].products.length; k++) {
                      const productId = packages[i].products[k].product_id
                      let isFound = false
                      for (let m = 0; m < products2.length; m++) {
                        const productId2 = products2[m].product_id
                        if (productId === productId2) {
                          // 商品IDが見つかった場合
                          // 書籍一覧APIの書籍リストを
                          // パッケージ・商品一覧APIの対象商品の書籍リストとして設定する
                          packages[i].products[k].books = products2[m].books
                          isFound = true
                        }
                      }
                      if (!isFound) {
                        // 見つからない場合は、書籍リストを空配列で設定する
                        packages[i].products[k].books = []
                      }
                    }
                  }

                  const treeData = {
                    id: 0,
                    name: '',
                    packages: packages
                  }

                  // パッケージ＆商品APIと書籍APIのノードにユニークなID(数値)の重複が発生するため、
                  // ツリーデータの全ノードにユニークなID(数値)を設定し直す
                  const __param = { id: 10000 } // 分かりやすくpackage_id/product_id/book_idと被らないIDから始める
                  common.addUniqueIdForTreeData(treeData, 'id', ['packages', 'products', 'books'], __param)
                  treeData.id = 0 // ルートノードのidは必ず0

                  // 絞り込みツリーの全ノードの初期化
                  const parentNodeMap = {}
                  group1.initNodes(treeData, parentNodeMap)

                  if (isInit) {
                    // init() から実行された場合
          
                    if (Object.keys(store.state.qa.idMapListOfCheckedBooks).length !== 0) {
                      // 絞り込み条件の設定（商品ID&書籍ID）がストアに保持されている場合、チェック状態の復元を行う

                      // パッケージ書籍のチェックの復元
                      const idMapListOfCheckedBooks = store.state.qa.idMapListOfCheckedBooks
                      group1.restoreNodesCheck(treeData, idMapListOfCheckedBooks, parentNodeMap)

                      // 単体書籍のチェックの復元
                      const singleBooksWork = []
                      for (let i = 0; i < books.length; i++) {
                        const productId = Number(books[i].product_id)
                        const bookId = Number(books[i].book_id)
                        for (let k = 0; k < idMapListOfCheckedBooks.length; k++) {
                          const _productId = Number(idMapListOfCheckedBooks[k].product_id ?? 0)
                          const _bookId = Number(idMapListOfCheckedBooks[k].book_id ?? 0)
                          if (productId === _productId && bookId === _bookId) {
                            singleBooksWork.push(productId + ',' + bookId)
                            break
                          }
                        }
                      }
                      group1.single_books = singleBooksWork
                    }
                  }
            
                  // initNodes()で生成したデータを設定する
                  group1.packagesAndProductsAreaTreeData = treeData
                  group1.packagesAndProductsAreaBooks = books
                  group1.parentNodeMap = parentNodeMap

                  group1.isListPackagesAndProductsLoaded = true
                }
              })
              .catch(e => {
                group1.isListPackagesAndProductsLoaded = true
              })
          }
        })
        .catch(e => {
          group1.isListPackagesAndProductsLoaded = true
        })

    },
    
    /**
     * 検索結果一覧エリアを更新
     */
    updateSearchResultArea: () => {
    
      group1.isQAListLoaded = false

      // 利用者設定取得APIをコール
      const params = {}
      if (store.state.common.device === 'sp') {
        params.setting_key = 'sp_qa_order_disp_settings'
      } else {
        params.setting_key = 'pc_qa_order_disp_settings'
      }

      apiCommon.fetchUserSetting(params)
        .then(response => {
          if (response) {
            const { data } = response

            // 並び順設定・表示設定の初期化
            const listSettings = {}
            listSettings.per_page = store.state.qa.list_per_page
            listSettings.hidden_keys = store.state.qa.list_hidden_keys
            listSettings.order = store.state.qa.list_order

            // 取得した値で上書き
            try {
              if (data.setting_value && data.setting_value !== '') {
                const data2 = JSON.parse(data.setting_value)

                if (typeof data2.per_page !== 'undefined') {
                  listSettings.per_page = data2.per_page
                }
                if (typeof data2.hidden_keys !== 'undefined') {
                  listSettings.hidden_keys = data2.hidden_keys
                }
                if (typeof data2.order !== 'undefined') {
                  listSettings.order = data2.order
                }
              }
            } catch (e) {
            }

            // ストアに保存
            store.dispatch('qa/setListDisplaySettings', { perPage: listSettings.per_page, hiddenKeys: listSettings.hidden_keys })
            store.dispatch('qa/setListOrderSettings', listSettings.order)
           
            // QA一覧APIをコール
            const basicParams = {}
            basicParams.start = (group1.currentPage - 1) * listSettings.per_page
            basicParams.per_page = listSettings.per_page
            basicParams.search_order = listSettings.order
            if (listSettings.order === 'score') {
              basicParams.ascending = 'desc'
            } else if (listSettings.order === 'book') {
              basicParams.ascending = 'asc'
            }
            // ページネートフラグ
            basicParams.is_paginate = group1.isPaginate ? 1 : 0

            // 検索パラメータ(一覧API、件数取得API共通)
            let searchParams = {}

            if (group1.isSearchDetail) {
              // 詳細検索の場合（詳細検索ウィンドウで検索実行後、ページネーションを実行した場合）
              searchParams = store.state.search.qa
            } else {
              // 簡易検索の場合
              if (group1.search_text !== '') {
                // キーワードが入力されている場合
                searchParams.search_text = group1.search_text
              }
              searchParams.is_fuzzy = group1.is_fuzzy
            }

            const idMapListOfCheckedBooks = store.state.qa.idMapListOfCheckedBooks
            if (Object.keys(idMapListOfCheckedBooks).length > 0) {
              // 絞り込みで書籍にチェックが入っている場合
              searchParams.product_books = idMapListOfCheckedBooks
            } else {
              // 絞り込みで書籍にチェックが入っていない場合
              delete searchParams.product_books
            }

            const params = { ...basicParams, ...searchParams }

            apiQA.list(params)
              .then(response => {
        
                if (response) {
                  const { data } = response

                  const listWork = data.list ?? []

                  // 現在内容日付のフォーマットを整形
                  for (let i = 0; i < listWork.length; i++) {
                    const citationDate = listWork[i].citation_date ?? ''
                    if (citationDate !== '') {
                      listWork[i].citation_date = common.formatDate(citationDate)
                    }
                  }

                  group1.list = listWork

                  // リスト存在フラグ
                  group1.listExists = (group1.list.length > 0)

                  // 検索結果一覧エリアの表示状態をスクロール位置指定用IDの位置にスクロール
                  if (group1.listExists) {
                    group1.scrollMainAreaToPrevItem()
                  }

                  group1.isQAListLoaded = true
                }
              })
              .catch(e => {
                group1.isQAListLoaded = true
              })


            // データ件数ロード完了フラグをfalseに設定
            group1.isTotalItemLoaded = false  
            // ページネーション表示フラグ（2ページ以上の場合に表示）をfalseに設定
            group1.isPaginationShown = false

            // キーワード検索結果件数取得APIをコール
            const basicParams2 = {}
            basicParams2.search_range = 3 // 検索対象：3=QA

            const countParams = { ...basicParams2, ...searchParams }

            apiQA.countKeywordSearchResults(countParams)
              .then(response => {
        
                if (response) {
                  const { data } = response

                  // 全データ件数
                  group1.totalItems = data.count

                  // ページネーション表示フラグ（2ページ以上の場合に表示）
                  group1.isPaginationShown = (group1.totalItems > group1.perPage)
                }

                group1.isTotalItemLoaded = true
              })
              .catch(e => {
                group1.isTotalItemLoaded = true
              })
          }
          group1.isQAListSettingsLoaded = true
        })
        .catch(e => {
          group1.isQAListSettingsLoaded = true
        })
    },

    /**
     * 検索結果一覧エリアの表示状態をスクロール位置指定用IDの位置にスクロール
     */
    scrollMainAreaToPrevItem: () => {

      // 遷移元
      const fromName = store.state.common.fromName
      // 詳細画面から一覧画面に戻ってきた時のスクロール位置指定用ID
      const selectedListItemId = store.state.qa.selectedListItemId

      if (fromName === 'PCQADetail' || fromName === 'SPQADetail') {
        // 詳細画面から遷移してきた場合
        if (selectedListItemId === 0) {
          const scrollArea = document.getElementById('scroll-target')
          if (scrollArea) {
            scrollArea.scrollTop = 0
          }
        } else {
          // Vue.nextTick()の場合、対象htmlIdを取得できない場合がある。
          // ツリー描画に時間がかかるのではと思われる。
          // そのため、setInterval()でスクロール先要素を取得するまでループする
          let count = 0
          const intervalId = setInterval(() => {
            const htmlId = 'list-item-' + selectedListItemId
            const target = document.getElementById(htmlId)
            if (target) {
              const targetRect = target.getBoundingClientRect()
              const scrollArea = document.getElementById('scroll-target')
              if (scrollArea) {
                const areaRect = scrollArea.getBoundingClientRect()
                const scrollY = scrollArea.scrollTop + targetRect.top - areaRect.top
                scrollArea.scrollTop = scrollY
                // 詳細画面から一覧画面に戻ってきた時のスクロール位置指定用IDをクリア
                store.dispatch('qa/setSelectedListItemId', 0)
                clearInterval(intervalId)
              }  
            }
            count++
            if (count >= SCROLL_INTERVAL_MAX_COUNT) {
              clearInterval(intervalId)
            }
          }, SCROLL_INTERVAL_DELAY)
        }
      }
    },

    /**
    * 絞り込みツリーの全ノードの初期化
    */
    initNodes: (node, parentNodeMap, parentNode = null) => {

      node.isChecked = false

      let parentNodeMapKey = ''
      if (typeof node.product_id !== 'undefined' && typeof node.book_id !== 'undefined') {
        node.itemType = 'book'
        parentNodeMapKey = 'book_' + node.product_id + '_' + node.book_id
      } else if (typeof node.product_id !== 'undefined') {
        node.itemType = 'product'
        parentNodeMapKey = 'product_' + node.product_id
      } else if (typeof node.package_id !== 'undefined') {
        node.itemType = 'package'
        parentNodeMapKey = 'package_' + node.package_id
      }
      parentNodeMap[parentNodeMapKey] = parentNode

      if (typeof node.packages !== 'undefined') {
        for (let i = 0; i < node.packages.length; i++) {
          group1.initNodes(node.packages[i], parentNodeMap, node)
        }
      }
      if (typeof node.products !== 'undefined') {
        for (let i = 0; i < node.products.length; i++) {
          group1.initNodes(node.products[i], parentNodeMap, node)
        }
      }
      if (typeof node.books !== 'undefined') {
        for (let i = 0; i < node.books.length; i++) {
          group1.initNodes(node.books[i], parentNodeMap, node)
        }
      }
    },

    /**
     * 絞り込みツリーのチェック状態を復元する
     */
    restoreNodesCheck: (node, idMapListOfCheckedBooks, parentNodeMap) => {
      group1._restoreNodesCheckStep1(node, idMapListOfCheckedBooks, parentNodeMap)
      group1._restoreNodesCheckStep2(node, idMapListOfCheckedBooks, parentNodeMap)
    },

    /**
     * 絞り込みツリーのチェック状態を復元する：ステップ１
     * 最下層の書籍のチェックフラグを設定する
     */
    _restoreNodesCheckStep1: (node, idMapListOfCheckedBooks, parentNodeMap) => {
      if (node.itemType === 'book') {
        const productId = Number(node.product_id)
        const bookId = Number(node.book_id)
        const name = node.name

        for (let i = 0; i < idMapListOfCheckedBooks.length; i++) {
          const idMap = idMapListOfCheckedBooks[i]
          if (idMap) {
            const _productId = Number(idMap.product_id ?? 0)
            const _bookId = Number(idMap.book_id ?? 0)

            if (productId === _productId && bookId === _bookId) {
              // 書籍のチェックフラグを設定する
              node.isChecked = true
            }
          }
        }
      }

      if (typeof node.packages !== 'undefined') {
        for (let i = 0; i < node.packages.length; i++) {
          group1._restoreNodesCheckStep1(node.packages[i], idMapListOfCheckedBooks, parentNodeMap)
        }
      }
      if (typeof node.products !== 'undefined') {
        for (let i = 0; i < node.products.length; i++) {
          group1._restoreNodesCheckStep1(node.products[i], idMapListOfCheckedBooks, parentNodeMap)
        }
      }
      if (typeof node.books !== 'undefined') {
        for (let i = 0; i < node.books.length; i++) {
          group1._restoreNodesCheckStep1(node.books[i], idMapListOfCheckedBooks, parentNodeMap)
        }
      }
    },

    /**
     * 絞り込みツリーのチェック状態を復元する：ステップ２
     * 同階層の全要素がチェックされていたら親要素をチェックする。そうれでなければ、親要素のチェックを外す。
     */
    _restoreNodesCheckStep2: (node, idMapListOfCheckedBooks, parentNodeMap) => {
      if (node.itemType === 'book') {
        const productId = Number(node.product_id)
        const bookId = Number(node.book_id)
        const name = node.name

        for (let i = 0; i < idMapListOfCheckedBooks.length; i++) {
          const idMap = idMapListOfCheckedBooks[i]
          if (idMap) {
            const _productId = Number(idMap.product_id ?? 0)
            const _bookId = Number(idMap.book_id ?? 0)

            if (productId === _productId && bookId === _bookId) {
              // 同階層の全要素がチェックされていたら親要素をチェックする。そうれでなければ、親要素のチェックを外す。
              setParentNodeCheckStatusRecursively(node, parentNodeMap)
            }
          }
        }
      }

      if (typeof node.packages !== 'undefined') {
        for (let i = 0; i < node.packages.length; i++) {
          group1._restoreNodesCheckStep2(node.packages[i], idMapListOfCheckedBooks, parentNodeMap)
        }
      }
      if (typeof node.products !== 'undefined') {
        for (let i = 0; i < node.products.length; i++) {
          group1._restoreNodesCheckStep2(node.products[i], idMapListOfCheckedBooks, parentNodeMap)
        }
      }
      if (typeof node.books !== 'undefined') {
        for (let i = 0; i < node.books.length; i++) {
          group1._restoreNodesCheckStep2(node.books[i], idMapListOfCheckedBooks, parentNodeMap)
        }
      }
    },

    /**
     * 絞り込みツリーの全ノードのチェック状態を設定
     */
    setAllNoodesCheck: (node, isChecked) => {
      node.isChecked = isChecked
      if (typeof node.packages !== 'undefined') {
        for (let i = 0; i < node.packages.length; i++) {
          group1.setAllNoodesCheck(node.packages[i], isChecked)
        }
      }
      if (typeof node.products !== 'undefined') {
        for (let i = 0; i < node.products.length; i++) {
          group1.setAllNoodesCheck(node.products[i], isChecked)
        }
      }
      if (typeof node.books !== 'undefined') {
        for (let i = 0; i < node.books.length; i++) {
          group1.setAllNoodesCheck(node.books[i], isChecked)
        }
      }
    },

    /**
     * チェックされた書籍の商品ID&書籍IDリスト検出し、
     * ストア変数idMapListOfCheckedBooksに設定する
     */
    setIdMapListOfCheckedBooks: () => {

      const idMapListOfCheckedBooks = []

      // パッケージ＆商品＆書籍ツリーでチェックされている書籍の書籍IDリストを取得する
      const treeData = group1.packagesAndProductsAreaTreeData
      getIdMapListOfCheckedBooks(treeData, idMapListOfCheckedBooks)

      // 単体書籍リストでチェックされている書籍の商品ID&書籍IDリストを取得し、リストに追加
      const idsOfSingleBooks = group1.single_books

      for (let i = 0; i < idsOfSingleBooks.length; i++) {
        if (idsOfSingleBooks[i] !== '') {
          const arr = idsOfSingleBooks[i].split(',')
          const productId = Number(arr[0]) ?? 0
          const bookId = Number(arr[1]) ?? 0
          const map = {
            product_id: productId,
            book_id: bookId
          }
          idMapListOfCheckedBooks.push(map)
        }
      }

      // 設定
      store.dispatch('qa/setIdMapListOfCheckedBooks', idMapListOfCheckedBooks)

    },

    /**
     * 検索条件リセット
     */
    searchResetOptions: () => {

      // 検索タイプ
      group1.search_type = 'keyword'

      // 検索UI(PCSearchUI/SPSearchUI)の検索テキストと曖昧フラグを更新する
      store.dispatch('searchUI/setSearchInputOfSearchUI', { searchText: '', isFuzzy: 0 })
      store.dispatch('searchUI/setSearchInputOfSearchUIParam', { searchCategory: group1.searchCategory, searchText: '', isFuzzy: 0 })

      if (group1.isSearchDetail) {
        // 詳細検索の場合

        // 絞り込みを初期化
        store.dispatch('qa/setIdMapListOfCheckedBooks', [])

        // 利用者設定更新APIをコール (QA詳細検索設定データを保存)
        const params = {}
        if (store.state.common.device === 'sp') {
          params.setting_key = 'sp_qa_detail_search_settings'
        } else {
          params.setting_key = 'pc_qa_detail_search_settings'
        }
        params.setting_value = '' // クリア

        apiCommon.updateUserSetting(params)
          .then(response => {
            if (response) {
              const { data } = response

              // ストアに保持していた検索条件をクリア
              store.dispatch('search/setQASearchParam', {search_text: ''})
              store.dispatch('search/setQASearchExtractParam', {search_text: ''})

              // 詳細検索実行フラグをfalseに設定
              store.dispatch('qa/setSearchDetail', false)

              // 検索
              group1.searchWithOptions({ is_init: true, search_executed: true, is_reset: true })
            }
          })
      } else {
        // 簡易表示の場合

        // 検索
        group1.searchWithOptions({ is_init: true, search_executed: true, is_reset: true })
      }
    },

    /**
     * 検索を実行する/詳細検索を実行する
     */
    search: () => {
      const options = {}
      group1.searchWithOptions(options)
    },

    /**
     * 検索を実行する/詳細検索を実行する（オプション指定）
     */
    searchWithOptions(options) {

      let searchCategory
      let searchText
      let isFuzzy
      if (
        typeof options !== 'undefined' &&
        typeof options.searchCategory !== 'undefined'
      ) {
        searchCategory = options.searchCategory
        searchText = options.search_text
        isFuzzy = options.is_fuzzy
      } else {
        searchCategory = group1.searchCategory
        searchText = group1.search_text
        isFuzzy = group1.is_fuzzy
      }

      if (searchCategory !== 'qa') {
        // 指定した検索カテゴリに対するキーワード検索を実行し、対象の検索結果画面に遷移する
        searchByKeyword(context, searchCategory, searchText, isFuzzy) 
        return
      }

      // init()から実行されたか
      let isInit = false
      if (typeof options.is_init !== 'undefined') {
        isInit = options.is_init
      }

      // 検索をリセット実行してから実行されたか
      let isReset = false
      if (typeof options.is_reset !== 'undefined') {
        isReset = options.is_reset
      }

      // // メニューからの処理か
      // let isFromMenu = false
      // if (typeof options.from_menu !== 'undefined') {
      //   isFromMenu = options.from_menu
      // }

      // キーワード検索が実行されたか？
      // (1)QA一覧の簡易検索キーワード入力欄でEnterを押した
      // (2)QA一覧の簡易検索キーワード入力欄の検索ボタンを押した場合
      // (3)メニューの簡易検索キーワード入力欄でEnterを押した
      // (4)メニューの簡易検索キーワード入力欄の検索ボタンを押した場合
      // (5)詳細検索を実行した場合
      // ※他の画面から遷移してきた、詳細画面から戻ってきたなどの場合はfalse
      let isSearchExecutedAtSameScreen = false
      if (typeof options.search_executed !== 'undefined') {
        isSearchExecutedAtSameScreen = options.search_executed
      }

      // 検索カテゴリを設定（init()で設定されると思うが念のため設定）
      store.dispatch('search/setSearchCategory', 'qa')

      // リスト表示時のパラメータの初期化
      group1.initializeParametersForList()
      
      if (store.state.common.device === 'sp') {
        // キーワード入力欄のフォーカス表示フラグをfalseに設定
        state.isFocusInput = false
        // キーワード検索ウィンドウを閉じる
        state.visibleSearchByKeyword = false
      }

      if (isSearchExecutedAtSameScreen) {
        // 同画面での検索の場合

        // 絞り込みのチェックをすべてクリア
        // パッケージ商品
        const treeData = group1.packagesAndProductsAreaTreeData
        group1.setAllNoodesCheck(treeData, false)
        group1.packagesAndProductsAreaTreeData = treeData
        // 単体書籍
        group1.single_books = []
        group1.packagesAndProductsAreaBooks = []
        // ストアに保持している絞り込み情報をクリア
        store.dispatch('qa/setIdMapListOfCheckedBooks', [])

        // 初期化
        group1.currentPage = 1 // 現在のページ
        group1.isPaginate = false // ページネートフラグ
      }
  
      // 初期化
      group1.totalItems = 0 // 全データ件数
  
      if (group1.isSearchDetail) {
        // 詳細検索の場合

        // 詳細検索実行フラグをtrue
        store.dispatch('qa/setSearchDetail', true)

        // モードを「search: キーワード検索モード」に設定
        group1.mode = 'search'

      } else {
        // 簡易表示の場合

        // 詳細検索実行フラグをfalse
        store.dispatch('qa/setSearchDetail', false)

        if (searchText === '' || isReset) {
          // モードを「list: 一覧表示モード」に設定
          group1.mode = 'list'
        } else {
          // モードを「search: キーワード検索モード」に設定
          group1.mode = 'search'
        }

        // 検索条件をストアに保持
        // (参考：詳細検索についてはQASearchDetailで保持している)
        const searchParams = {
          search_text: searchText,
          is_fuzzy: isFuzzy
        }
        store.dispatch('search/setQASearchExtractParam', searchParams)
        store.dispatch('search/setQASearchParam', searchParams)
      }

      // 絞り込みエリア（パッケージ・商品一覧エリア）を更新
      group1.updatePackageAndProductsArea(isInit)

      // メインエリア(検索結果一覧)の表示処理
      group1.updateSearchResultArea()
    },

    /**
     * パッケージ＆商品一覧ツリーのいずれかのチェックボックスがチェックされた場合
     */
    onPackageAndProductsTreeItemClicked: ({nodeId, targetIdsMap}) => {

      group1.isTotalItemLoaded = false

      // 絞り込みツリー：チェック状態を伝播させる
      const treeData = group1.packagesAndProductsAreaTreeData
      propagateCheckedRecursively(treeData, nodeId, group1.parentNodeMap)

      // 検索一覧状態管理パラメータを初期化
      group1.totalItems = 0 // 全データ件数
      group1.currentPage = 1 // 現在のページ
      group1.isPaginate = false // ページネートフラグ

      // チェックされた書籍の商品ID&書籍IDリストをストアに保存
      group1.setIdMapListOfCheckedBooks()

      // 検索結果一覧エリアを更新
      group1.updateSearchResultArea()

      if (store.state.common.device === 'sp') {
        store.dispatch('common/showToast', { text: '絞り込み条件を変更しました' })
      }
    },

    /**
     * 単体書籍リストのいずれかのチェックボックスがチェックされた場合
     */
    onSingleBookClicked: () => {

      group1.isTotalItemLoaded = false

      // 検索一覧状態管理パラメータを初期化
      group1.totalItems = 0 // 全データ件数
      group1.currentPage = 1 // 現在のページ
      group1.isPaginate = false // ページネートフラグ

      // 絞り込みのチェック状態を設定
      const idsOfSingleBooks = group1.single_books
      const books = group1.packagesAndProductsAreaBooks
      for (let i = 0; i < books.length; i++) {
        const productId = books[i].product_id
        const bookId = books[i].book_id
        let isFound = false
        for (let k = 0; k < idsOfSingleBooks.length; k++) {
          if (idsOfSingleBooks[k] !== '') {
            const arr = idsOfSingleBooks[k].split(',')
            const _productId = Number(arr[0]) ?? 0
            const _bookId = Number(arr[1]) ?? 0
            if (productId === _productId && bookId === _bookId) {
              isFound = true
              break
            }
          }
        }
        books[i].isChecked = isFound
      }
      group1.packagesAndProductsAreaBooks = books

      // チェックされた書籍の商品ID&書籍IDリストをストアに保存
      group1.setIdMapListOfCheckedBooks()

      // 検索結果一覧エリアを更新
      group1.updateSearchResultArea()

      if (store.state.common.device === 'sp') {
        store.dispatch('common/showToast', { text: '絞り込み条件を変更しました' })
      }
    },

    /**
     * ページが変更された場合（ページネーションをクリックした場合）
     */
    onPageChanged: ({ currentPage, start }) => {

      // 新しいページの情報を設定
      group1.currentPage = currentPage
      // ページネートフラグは、ページ切り替えのためtrue
      group1.isPaginate = true

      // チェックされた書籍のIDリストを設定する
      group1.setIdMapListOfCheckedBooks()

      // 検索結果一覧エリアを更新
      group1.updateSearchResultArea()
    },

    /**
     * 検索結果一覧内のQAタイトルをクリックした場合
     */
    onQATitleInListClicked: (bookId, bookName, qaId) => {

      // 詳細画面から一覧画面に戻ってきた時のスクロール位置指定用ID
      store.dispatch('qa/setSelectedListItemId', qaId)

      // 書籍ID、QA IDを保存
      store.dispatch('qa/setBookId', bookId)
      store.dispatch('qa/setBookName', bookName)
      store.dispatch('qa/setQaId', qaId)
      store.dispatch('qa/setSearchIndexId', 0)
      store.dispatch('qa/setNumberCarouselVisible', 0)

      // QA詳細画面に遷移
      if (store.state.common.device === 'sp') {
        router.push({ name: 'SPQADetail', params: { qaId: qaId } })
      } else {
        router.push({ name: 'PCQADetail', params: { qaId: qaId } })
      }
    },

    /**
     * 検索結果一覧内の書式ダウンロードをクリックした場合
     */
    onDocumentDownloadsInListClicked: (bookId, bookName, qaId) => {

      // 書籍ID、QA IDを保存
      store.dispatch('qa/setBookId', bookId)
      store.dispatch('qa/setBookName', bookName)
      store.dispatch('qa/setQaId', qaId)
      store.dispatch('qa/setSearchIndexId', 0)
      store.dispatch('qa/setNumberCarouselVisible', 0)

      // 検索結果のクリア
      // 検索UI(PCSearchUI/SPSearchUI)の検索テキストと曖昧フラグを更新する
      store.dispatch('searchUI/setSearchInputOfSearchUI', { searchText: '', isFuzzy: 0 })
      store.dispatch('searchUI/setSearchInputOfSearchUIParam', { searchCategory: 'download', searchText: '', isFuzzy: 0 })

      // ストアに保持している検索条件をクリア
      store.dispatch('search/setDownloadSearchParam', {})

      // ストアに保持している絞り込み情報をクリア
      store.dispatch('documentDownloadsList/setPackagesAndProductsAreaTreeData', {})
      store.dispatch('documentDownloadsList/setPackagesAndProductsAreaBooks', [])
      store.dispatch('documentDownloadsList/setIdMapListOfCheckedProducts', [])

      // モードを「list: 一覧表示(ドリルダウン)モード」に設定
      store.dispatch('documentDownloadsList/setMode', 'list')

      // 選択している書式に対応する書籍IDを保持
      const selectedIdMap = {
        book_id: bookId
      }
      store.dispatch('documentDownloadsList/setSelectedIdMapOfBooksArea', selectedIdMap)

      // 書式DL画面に遷移
      if (store.state.common.device === 'sp') {
        // SPに書式DL画面は無い
      } else {
        router.push({ name: 'PCDocumentDownloadsList', params: {} })
      }
    },

    /**
     * 検索結果一覧内の書籍名をクリックした場合
     */
    onBookInListClicked: (bookId, bookName) => {

      // 遷移先のQA詳細ではQA IDが必須であるため、対象書籍の目次の最初の設問のQA IDを指定する

      // 書籍目次一覧APIをコール
      const params = {}
      params.book_id = bookId
      params.search_index_id = 0

      apiQA.listBookTableOfContents(params)
        .then(response => {
          if (response) {
            const { data } = response

            const list = data.list ?? []
            const qaId = list[0].id ?? 0

            store.dispatch('qa/setBookId', bookId)
            store.dispatch('qa/setBookName', bookName)
            store.dispatch('qa/setQaId', qaId)
            store.dispatch('qa/setSearchIndexId', 0)
            store.dispatch('qa/setNumberCarouselVisible', 0)
      
            if (store.state.common.device === 'sp') {
              router.push({ name: 'SPQADetail', params: { qaId: qaId } })
            } else {
              router.push({ name: 'PCQADetail', params: { qaId: qaId } })
            }
          }
        })
    },

    // ノートの選択ウィンドウ（一覧画面用）に渡すQAID
    targetQaIdToSelectNote: 0,

    /**
     * 検索結果一覧内のノートをクリックした場合
     */
    onNoteInListClicked: (qaId) => {
      group1.targetQaIdToSelectNote = qaId
      state.visibleSelectNote = true
    },

    /**
     * 後で見るの状態が変わった場合
     */
    onViewLaterChanged: (result) => {

      const qaId = result.id
      const afterId = result.afterId

      const listWork = group1.list
      for (let i = 0; i < listWork.length; i++) {
        if (listWork[i].id === qaId) {
          listWork[i].after_id = afterId
          break
        }
      }
      group1.list = listWork
    },

    /**
     * 検索条件の保存をクリックした場合
     */
    onSaveSearchConditionClicked: () => {

      const map = {}

      // 検索条件追加APIをコール
      const params = {}
      params.key = 'qa'

      if (group1.isSearchDetail) {
        // 詳細検索の場合
        const searchDetailConditionParams = store.state.search.qa

        if (searchDetailConditionParams.search_type === 'andor') {
          // AND/OR検索の場合
          let searchText = ''
          if (
            typeof searchDetailConditionParams.search_text !== 'undefined' &&
            searchDetailConditionParams.search_text.length > 0 &&
            typeof searchDetailConditionParams.search_text[0].or !== 'undefined' &&
            searchDetailConditionParams.search_text[0].or.length > 0
          ) {
            searchText = searchDetailConditionParams.search_text[0].or[0]
          }
          if (searchText !== '') {
            params.title = 'Q＆A・事例（詳細検索）：' + searchText
          } else {
            params.title = 'Q＆A・事例（詳細検索）：キーワードなし'
          }
        } else {
          // キーワード検索の場合
          if (searchDetailConditionParams.search_text !== '') {
            params.title = 'Q＆A・事例（詳細検索）：' + searchDetailConditionParams.search_text 
          } else {
            params.title = 'Q＆A・事例（詳細検索）：キーワードなし'
          }
        }

      } else {
        // 簡易検索の場合
        if (group1.search_text !== '') {
          const searchText = group1.search_text ?? ''
          params.title = 'Q＆A・事例：' + searchText
        } else {
          params.title = 'Q＆A・事例：キーワードなし'
        }
      }

      const searchParams = []
      // 詳細検索実行フラグ(0:簡易検索、1:詳細検索)
      searchParams.push({ key: 'is_search_detail', value: group1.isSearchDetail ? 1 : 0 })
      if (group1.isSearchDetail) {
        // 詳細検索の場合
        const hash = store.state.search.qa
        for (const key in hash) {
          const value = hash[key]
          searchParams.push({ key: key, value: value })
        }
      } else {
        // 簡易検索の場合
        searchParams.push({ key: 'search_text', value: group1.search_text })
        searchParams.push({ key: 'is_fuzzy', value: group1.is_fuzzy })
        const idMapListOfCheckedBooks = store.state.qa.idMapListOfCheckedBooks
        searchParams.push({ key: 'product_books', value: idMapListOfCheckedBooks })
      }

      // QA一覧の絞り込み表示用の検索条件
      const hashExtract = store.state.search.qa_extract
      for (const key in hashExtract) {
        const key2 = PREFEX_EXTRACT_CONDITION + key // 接頭辞「extract:」を付与する
        const value = hashExtract[key]
        searchParams.push({ key: key2, value: value })
      }

      params.search = searchParams

      apiSearchCondition.add(params) 
        .then(response => {
          if (response) {
            const { data } = response

            store.dispatch('common/showToast', { text: '検索条件を保存しました。' })
          }
        })
    },

    /**
     * SPで検索ウィンドウ表示ボタンをクリックした場合
     */
    onOpenSearchWindowButtonClicked: () => {
      if (store.state.common.device === 'sp') {
        if (group1.isSearchDetail) {
          // 詳細検索の場合はダイレクトに詳細検索ウィンドウを表示
          state.visibleSearchDetail = true
          state.visibleSearchExtract = false
          state.visibleMenu = false
          state.visibleSearchByKeyword = false
        } else {
          // 簡易検索の場合
          state.visibleSearchByKeyword = !state.visibleSearchByKeyword
        }
      }
    }

  })

  const state = reactive({
    visibleMenu: false,
    visibleSearchByKeyword: false,
    visibleSearchDisplaySettings: false,
    visibleMenuLeft: false,
    visibleSearchExtract: false,
    visibleSearchOrderSettings: false,
    visibleSearchDetail: false, // 詳細検索ウィンドウ表示フラグ
    visibleSelectNote: false,
    visibleSearchDisplaySort: false, // PC用
    isOpenMenuArrow: false, // PC用
    visibleBasicToastSave: false,
    isFocusInput: false // SP用:キーワード入力欄のフォーカス表示フラグ
  })

  const searchSelect = reactive({
    isFocusInput: false,
    currentValue: 0
  })

  const clickSearchHistory = () => {
    store.commit('searchHistory/setFilterTypes', ['qa'])
    router.push({ name: 'PCHistoryOfSearch' })
  }

  return {
    group1, 
    state,
    searchSelect,
    clickSearchHistory
  }
}
